import { usePublicEventContext } from '@event/contexts/PublicEventContext';
import { AddEventToCalendar } from '@event/routes/public/EventDetail/EventDetailCard';
import { useAppSelector } from '@hooks/generic';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import UpdateIcon from '@mui/icons-material/Update';
import { Divider, Typography } from '@mui/material';
import { getDateFnsLocale } from '@utils/common';
import { differenceInCalendarDays, parseISO } from 'date-fns';
import { format } from 'date-fns-tz';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

const EventDetailCardDates = () => {
    const {
        event: { data: event },
    } = usePublicEventContext();

    const currentLocale = useAppSelector((state) => state.translation.locale);
    const dateLocale = getDateFnsLocale(currentLocale);

    const daysToGo = useMemo(() => {
        if (!event?.date_start) return '';
        const startDate = event?.date_end ? parseISO(event.date_start) : null;
        const endDate = event?.date_end ? parseISO(event.date_end) : null;
        const today = new Date();

        if (startDate && endDate) {
            if (today >= startDate && today <= endDate) {
                return 0;
            } else return differenceInCalendarDays(startDate, today);
        } else {
            return '';
        }
    }, [event?.date_start, event?.date_end]);

    return (
        <div className="flex flex-col gap-5 px-3.5 md:px-0 md:justify-between md:flex-row md:gap-0">
            <div className="grid grid-cols-[auto_1fr_auto] grid-rows-2 [grid-template-areas:'a_b_c''d_e_f''g_h_i''z_z_z''j_k_l''m_n_o'] w-full">
                <p className="font-bold text-sm flex items-center gap-1">
                    <UpdateIcon />
                    <FormattedMessage id="common.startDate" defaultMessage="START DATE" />
                </p>
                <div className="[grid-area:c] md:[grid-area:d]">
                    <p className="font-normal text-sm">
                        {event?.date_start
                            ? format(event?.date_start, 'EEEE, dd MMM, yyyy', { locale: dateLocale })
                            : ''}
                    </p>
                    <p className="text-xs text-gray51 underline font-semibold">
                        {event?.date_start ? format(event?.date_start ?? '', 'hh:mm aa O', { locale: dateLocale }) : ''}
                    </p>
                </div>
                <p className="font-bold text-sm flex items-center gap-1 [grid-area:g] md:[grid-area:c]">
                    <UpdateIcon />
                    <FormattedMessage id="common.endDate" defaultMessage="END DATE" />
                </p>
                <div className="[grid-area:i] md:[grid-area:f]">
                    <p className="font-normal text-sm">
                        {event?.date_end ? format(event?.date_end, 'EEEE, dd MMM, yyyy', { locale: dateLocale }) : ''}
                    </p>
                    <p className="text-xs text-gray51 underline font-semibold">
                        {event?.date_end ? format(event?.date_end ?? '', 'hh:mm aa O', { locale: dateLocale }) : ''}
                    </p>
                </div>
                <AddEventToCalendar />
                <Divider className="[grid-area:j] !my-4" />
                <Divider className="[grid-area:k] !my-4" />
                <Divider className="[grid-area:l] !my-4" />
                <p className="flex items-center gap-2 font-semibold text-sm justify-center md:justify-start [grid-area:m]">
                    <FormattedMessage
                        id="event.daysToGo"
                        defaultMessage="{number} Days to go"
                        values={{ number: <span className="text-spanish-blue text-4xl font-bold">{daysToGo}</span> }}
                    />
                </p>
                {!event?.enable_external_registration ? (
                    <div className="[grid-area:o] text-sm pt-2">
                        <div className="flex items-center gap-2">
                            <PersonOutlineOutlinedIcon />
                            <Typography fontSize={14} color="neutral-900.main">
                                <FormattedMessage
                                    id="event.openSpots"
                                    defaultMessage="{number} Open spots"
                                    values={{
                                        number: (
                                            <Typography
                                                component="span"
                                                fontWeight={700}
                                                fontSize={14}
                                                color="neutral-900.main"
                                            >
                                                {Number(event?.max_capacity) - Number(event?.number_attendees)}
                                            </Typography>
                                        ),
                                    }}
                                />
                            </Typography>
                        </div>
                    </div>
                ) : null}
            </div>
        </div>
    );
};

export { EventDetailCardDates };
